import { useState, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { StatusBadge } from './ui/status-badge';
import { notify } from '../utils/notifications';
import {
  useAddOrgMembership,
  useToggleMembershipActive,
  useSetUserSystemRole,
  useSetOrgRole,
  type AdminUser,
  type AdminRole,
  type AdminOrganization,
} from '../api/extensions/useAdminOps';
import { Button } from './ui/button';

// The ALL sentinel org — a role scoped here is system-wide (= super-admin).
const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';

// Empty-state sentinel for the role select so a user with no role in an org
// can still render the dropdown without a placeholder/value mismatch warning.
const NO_ROLE = '__none__';

export interface ManageUserRolesFormProps {
  user: AdminUser;
  availableRoles: AdminRole[];
  availableOrgs: AdminOrganization[];
  isSystemAdmin: boolean;
  managedOrgIds?: string[]; // undefined = unrestricted (ALL-org admin); array = scoped
  currentUserProfileId?: string; // logged-in admin's user_profiles.id (to block self-edit of system role)
}

/**
 * Inline form for viewing and editing a user's roles, system access (super
 * admin toggle), and organization memberships. Rendered both inside
 * UserManagementModal (icon-triggered modal on Manage Users) and as a tab on
 * the user details page so admins have whichever surface is closer to hand.
 *
 * Invariant: a user has at most one role per organization. The per-org row
 * shows a single Select with the user's current role pre-selected; changing
 * it routes through `useSetOrgRole` which replaces the role atomically on
 * the backend. There is no "remove role" affordance — clearing the role
 * means picking "— No role —" from the same dropdown, and the membership
 * row itself can only be deactivated, never deleted.
 */
function ManageUserRolesForm({
  user,
  availableRoles,
  availableOrgs,
  isSystemAdmin,
  managedOrgIds,
  currentUserProfileId,
}: ManageUserRolesFormProps) {
  const [addOrgId, setAddOrgId] = useState<string>('');

  const addOrgMembership = useAddOrgMembership();
  const toggleActive = useToggleMembershipActive();
  const setSystemRole = useSetUserSystemRole();
  const setOrgRole = useSetOrgRole();
  // Tracks which org's role select is currently submitting so we can disable
  // just that one row instead of the whole form.
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  // Group roles by org — single-role-per-org invariant means each value
  // should have at most one entry, but we keep the array for the rare
  // legacy case where pre-migration data hadn't been consolidated yet.
  const rolesByOrg = useMemo(() => {
    const map = new Map<string | null, AdminUser['roles']>();
    for (const r of user.roles) {
      const key = r.organization_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return map;
  }, [user]);

  // Filter visible memberships by managed orgs (org-scoped admins only see their orgs)
  const visibleMemberships = useMemo(() => {
    if (!managedOrgIds) return user.organization_memberships; // unrestricted
    return user.organization_memberships.filter(
      (m) => managedOrgIds.includes(m.organization_id),
    );
  }, [user, managedOrgIds]);

  // Orgs the user is NOT already a member of (for "Add to Organization"), sorted alphabetically
  // Scoped to managedOrgIds for org-specific admins
  const availableOrgsForAdd = useMemo(() => {
    const orgsPool = managedOrgIds
      ? availableOrgs.filter((org) => managedOrgIds.includes(org.id))
      : availableOrgs;
    return orgsPool
      .filter(
        (org) => !user.organization_memberships.some((m) => m.organization_id === org.id),
      )
      .sort((a, b) =>
        ((a as any).short_name || a.name).localeCompare((b as any).short_name || b.name),
      );
  }, [user, availableOrgs, managedOrgIds]);

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.primary_email ||
    'Unknown';

  // ── System (ALL-org) role / super-admin ──────────────────────────────────
  const isTargetSuperAdmin = user.roles.some(
    (r) => r.organization_id === ALL_ORG_ID && r.role.role_name === 'admin',
  );
  const adminRoleId = availableRoles.find((r) => r.role_name === 'admin')?.id;
  const userRoleId = availableRoles.find((r) => r.role_name === 'user')?.id;
  const isSelf = !!currentUserProfileId && currentUserProfileId === user.id;
  // Only ALL-org admins see this section, and only when we know both role IDs.
  const showSystemAccessSection = isSystemAdmin && !!adminRoleId && !!userRoleId;

  const handleToggleSuperAdmin = async () => {
    if (isSelf) return;
    const targetRoleId = isTargetSuperAdmin ? userRoleId : adminRoleId;
    if (!targetRoleId) return;
    try {
      await setSystemRole.mutateAsync({ userId: user.id, roleId: targetRoleId });
      notify.success(
        isTargetSuperAdmin
          ? `${displayName} is no longer a super admin — they must sign out and back in.`
          : `${displayName} is now a super admin — they must sign out and back in.`,
      );
    } catch (err: any) {
      notify.error(err?.message || 'Failed to update system role');
    }
  };

  const handleSetOrgRole = async (orgId: string, nextValue: string) => {
    const nextRoleId = nextValue === NO_ROLE ? null : nextValue;
    const existing = rolesByOrg.get(orgId) || [];
    const currentRoleId = existing.length === 1 ? existing[0].role.id : null;
    if (nextRoleId === currentRoleId) return; // no-op
    setPendingOrgId(orgId);
    try {
      await setOrgRole.mutateAsync({
        userId: user.id,
        orgId,
        roleId: nextRoleId,
      });
      const label = nextRoleId
        ? (availableRoles.find((r) => r.id === nextRoleId)?.role_name ?? 'role')
        : 'no role';
      notify.success(`Role updated to ${label}`);
    } catch (err: any) {
      notify.error(err?.message || 'Failed to update role');
    } finally {
      setPendingOrgId(null);
    }
  };

  const handleAddOrg = async () => {
    if (!addOrgId) return;
    try {
      await addOrgMembership.mutateAsync({
        userId: user.id,
        organizationId: addOrgId,
      });
      notify.success('Organization membership added');
      setAddOrgId('');
    } catch (err: any) {
      notify.error(err?.message || 'Failed to add organization');
    }
  };

  const handleToggleActive = async (orgId: string) => {
    try {
      await toggleActive.mutateAsync({ userId: user.id, orgId });
      notify.success('Membership status toggled');
    } catch (err: any) {
      notify.error(err?.message || 'Failed to toggle status');
    }
  };

  /** Renders inline role management for a single org. */
  const renderRoleSection = (orgId: string) => {
    const orgRoles = rolesByOrg.get(orgId) || [];
    // Single-role-per-org: the dropdown's value is whichever role exists (or
    // NO_ROLE if none). If somehow there are pre-migration duplicates we
    // pick the first deterministically — picking a different role in the
    // dropdown will replace all of them in one PUT.
    const currentRoleId = orgRoles.length > 0 ? orgRoles[0].role.id : '';
    const currentRoleName = orgRoles[0]?.role.role_name;
    const isBusy = pendingOrgId === orgId && setOrgRole.isPending;
    const hasLegacyDuplicates = orgRoles.length > 1;

    return (
      <div className="mt-2 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground shrink-0">Role:</span>
          <Select
            value={currentRoleId || NO_ROLE}
            onValueChange={(val) => handleSetOrgRole(orgId, val)}
            disabled={isBusy}
          >
            <SelectTrigger className="flex-1 h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_ROLE}>— No role —</SelectItem>
              {availableRoles.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.role_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentRoleName && (
            <StatusBadge
              status={currentRoleName}
              variant="role"
              className="text-xs"
            />
          )}
        </div>
        {hasLegacyDuplicates && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Legacy: {orgRoles.length} roles found in this org — changing the
            dropdown will replace them with a single role.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Section: System Access (ALL-org admin / super admin) — only ALL-org admins see this */}
      {showSystemAccessSection && (
        <div className="border border-border rounded-lg p-4 bg-accent/30">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold flex items-center gap-2">
                System Access
                {isTargetSuperAdmin && (
                  <StatusBadge status="admin" variant="role" className="text-xs" />
                )}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {isTargetSuperAdmin
                  ? 'Super admin — full access across every organization. They must re-login after a change.'
                  : 'Grant super admin (system-wide admin across every organization). They must re-login after a change.'}
              </p>
            </div>
            {isSelf ? (
              <span className="text-xs italic text-muted-foreground whitespace-nowrap pt-0.5">
                you — ask another super admin
              </span>
            ) : (
              <Button
                variant={isTargetSuperAdmin ? 'outline' : 'default'}
                size="sm"
                onClick={handleToggleSuperAdmin}
                disabled={setSystemRole.isPending}
                className="whitespace-nowrap"
              >
                {setSystemRole.isPending
                  ? '...'
                  : isTargetSuperAdmin
                    ? 'Revoke Super Admin'
                    : 'Make Super Admin'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Section: Organization Memberships + Roles */}
      <div>
        <h3 className="text-base font-semibold mb-3">
          Organization Memberships & Roles
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Each organization grants one role. Change the role from the dropdown,
          or deactivate the membership to revoke access — memberships are kept
          on file so historical records stay linked.
        </p>
        {visibleMemberships.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {managedOrgIds
              ? 'No memberships in your managed organizations.'
              : 'No organization memberships. Add this user to an organization below to assign a role.'}
          </p>
        ) : (
          <div className="space-y-3">
            {visibleMemberships.map((mem) => (
              <div
                key={mem.organization_id}
                className="border border-border rounded-lg p-3"
              >
                {/* Org header */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {mem.organization.short_name || mem.organization.name}
                    </span>
                    <StatusBadge
                      status={mem.membership_status}
                      variant="registration"
                      className="text-xs"
                    />
                    {!mem.is_active && (
                      <span className="text-xs text-red-600 font-medium">Inactive</span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(mem.organization_id)}
                    disabled={toggleActive.isPending}
                    className="h-6 text-xs px-2"
                  >
                    {mem.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>

                {/* Single role select for this org */}
                {renderRoleSection(mem.organization_id)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section: Add to Organization */}
      {(isSystemAdmin || availableOrgsForAdd.length > 0) && (
        <div>
          <h3 className="text-base font-semibold mb-2">Add to Organization</h3>
          <p className="text-xs mb-3 text-muted-foreground">
            Adds the user to the organization with the default <code>member</code>{' '}
            role; change it from the dropdown above afterwards.
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Select
                value={addOrgId || undefined}
                onValueChange={(val) => setAddOrgId(val)}
              >
                <SelectTrigger className="w-full text-sm">
                  <SelectValue placeholder="Select organization..." />
                </SelectTrigger>
                <SelectContent>
                  {availableOrgsForAdd.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {(org as any).short_name || org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleAddOrg}
              disabled={!addOrgId || addOrgMembership.isPending}
              size="sm"
            >
              {addOrgMembership.isPending ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUserRolesForm;
